import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMediaQuery } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { getHeroData } from 'src/scripts/utils/loyalty-helpers';
import { BUILDER_API_KEY } from 'src/scripts/utils/environment';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { checkIfCustomer } from 'src/scripts/utils/helpers';
import { LoadingMoreSpinner } from '../../product-listing-page/components/SmallUIComponents';
import LoyaltySteps from './LoyaltySteps';
import { conditionallyOpenSignInModalOnClick } from '../../../globals';

let AccessHero = () => {
  const isMobileOrTablet = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });
  const isSignedIn = useMemo(() => checkIfCustomer(), []);

  const [heroData, setHeroData] = useState<AccessHeroContentData>();
  const [heroAsset, setHeroAsset] = useState<JSX.Element | null>(null);
  const [isHeroReady, setIsHeroReady] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (BUILDER_API_KEY) {
        const data = await getHeroData(BUILDER_API_KEY, 'loyalty-access-data');
        setHeroData(data);
      }
    };
    fetchData();
  }, []);

  // check if hero asset is image or video (the asset url doesn't end in a file type extension.)
  const checkHeroAssetType = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('Content-Type');
      return contentType;
    } catch (error) {
      return null;
    }
  };

  const renderLoyaltySteps = useCallback((heroData: AccessHeroContentData | undefined) => {
    const mountNode = document.querySelector('[react-render-target="loyalty-steps"]');
    if (mountNode) ReactDOM.render(<LoyaltySteps options={heroData} />, mountNode);
    else console.warn('missing render target: LoyaltySteps');
  }, []);

  useEffect(() => {
    renderLoyaltySteps(heroData);
  }, [heroData, renderLoyaltySteps]);

  const {
    buttonCta,
    dektopButtonBackgroundColor,
    desktopButtonTextColor,
    mobileButtonBackgroundColor,
    mobileButtonTextColor,
    unauthenticatedBenefitHeader,
    authenticatedBenefitHeader,
  } = heroData || {};

  const accessHeroPlaceholder = window.loyalty_settings && window.loyalty_settings.accessHeroPlaceholder;

  useEffect(() => {
    const updateHeroAsset = async () => {
      if (!heroData) return;
      const { mobilehero, desktophero } = heroData;
      const heroSrc = isMobileOrTablet ? mobilehero : desktophero;
      const heroSrcType = heroSrc && (await checkHeroAssetType(heroSrc));
      const heroProps = {
        className: `img-fluid ${isMobileOrTablet ? 'd-block d-md-none' : 'd-none d-md-block'}`,
        style: { width: isMobileOrTablet ? '100%' : 'auto' },
      };

      if (heroSrcType?.includes('video')) {
        setHeroAsset(
          <video {...heroProps} autoPlay loop playsInline muted onLoadedData={() => setIsHeroReady(true)}>
            <source src={heroSrc} type={heroSrcType} />
          </video>
        );
      } else {
        setHeroAsset(<img src={heroSrc} alt="hero" {...heroProps} onLoad={() => setIsHeroReady(true)} />);
      }
    };

    updateHeroAsset();
  }, [isMobileOrTablet, heroData]);

  return heroData ? (
    <>
      <div className={`hero ${isSignedIn ? 'signed-in' : 'signed-out'}`}>
        {heroAsset}
        {!isHeroReady ? (
          <img
            src={isMobileOrTablet ? accessHeroPlaceholder?.mobile : accessHeroPlaceholder?.desktop}
            alt="access hero placeholder"
            className="img-fluid hero-placeholder"
          />
        ) : (
          <div className="captions" style={{ alignItems: 'center' }}>
            {!isSignedIn && (
              <button
                className="sign-in"
                style={{
                  color: isMobileOrTablet ? mobileButtonTextColor : desktopButtonTextColor,
                  backgroundColor: isMobileOrTablet ? mobileButtonBackgroundColor : dektopButtonBackgroundColor,
                }}
                type="button"
                onClick={conditionallyOpenSignInModalOnClick}
              >
                {buttonCta}
              </button>
            )}
          </div>
        )}
      </div>
      {isHeroReady && (
        <div className="benefit-header">
          <p>{isSignedIn ? authenticatedBenefitHeader : unauthenticatedBenefitHeader}</p>
        </div>
      )}
    </>
  ) : (
    <>
      <img
        src={isMobileOrTablet ? accessHeroPlaceholder?.mobile : accessHeroPlaceholder?.desktop}
        alt="access hero placeholder"
        className="img-fluid"
      />
      <LoadingMoreSpinner isVisible className="redemption-hero-loading" />
    </>
  );
};

AccessHero = observer(AccessHero);
export { AccessHero };

const renderAccessHero = () => {
  const mountNode = document.querySelector('[react-render-target="loyalty-access-hero"]');

  if (mountNode) ReactDOM.render(<AccessHero />, mountNode);
  else console.warn('missing render target: RedemptionHero');
};

export const initRenderAccessHero = () => {
  renderAccessHero();
};

export interface AccessHeroContentData {
  buttonCta: string;
  dektopButtonBackgroundColor: string;
  desktopButtonTextColor?: string;
  desktophero: string;
  mobileButtonBackgroundColor: string;
  mobileButtonTextColor: string;
  mobilehero: string;
  textAlignment: string;
  authenticatedBenefitHeader: string;
  unauthenticatedBenefitHeader: string;
  authenticatedNumberedList: {
    textContent: [{ text: string }, { text: string }, { text: string }];
  };
  unauthenticatedNumberedList: {
    textContent: [{ text: string }, { text: string }, { text: string }];
  };
}
